import { colors } from '@beelday/common'
import styled from '@emotion/styled'
import { FC, useCallback, useEffect, useRef, useState } from 'react'

/**
 * How far up from the bottom edge the pointer wakes the bar. Thin on purpose:
 * the pointer stops at the screen edge, so a thin strip is as easy to hit as
 * a tall one, and a tall one sat on top of the stage's own bottom controls
 * (the quiz's Finish, for one) and swallowed their clicks.
 */
const HOT_ZONE_HEIGHT = 16

/**
 * Height the containing stage must keep free at its bottom edge, so that no
 * stage control ever sits under the hot zone.
 */
export const AUTO_HIDE_MENU_RESERVED_HEIGHT = HOT_ZONE_HEIGHT

/** Sliver left on screen at rest, so the bar is discoverable at all. */
const HANDLE_HEIGHT = 4

/**
 * Grace period after the pointer leaves. Without it, clipping the corner of
 * the bar on the way past the bottom of the screen makes it vanish mid-reach.
 */
const CLOSE_DELAY_MS = 400

/**
 * How far above the bar something inside it must reach to count as opened by
 * it - a popup menu, a bubble, the slide switcher. The handle reaches only
 * `HANDLE_HEIGHT`.
 */
const POPUP_REACH = HANDLE_HEIGHT * 2

/**
 * Whether an element inside the bar can be seen. Menu popups stay mounted
 * while closed - `opacity: 0` until hovered - so being laid out above the bar
 * is not enough.
 */
const isShown = (element: Element, bar: Element): boolean => {
	for (
		let node: Element | null = element;
		node && node !== bar;
		node = node.parentElement
	) {
		const style = getComputedStyle(node)
		if (
			style.display === 'none' ||
			style.visibility === 'hidden' ||
			style.opacity === '0'
		) {
			return false
		}
	}
	return true
}

/**
 * Whether the bar has something open: a visible descendant painted above it.
 * Menu items open their popups as DOM descendants positioned over the stage,
 * so this needs no knowledge of which item opened what.
 */
export const ownsOpenPopup = (bar: HTMLElement): boolean => {
	const top = bar.getBoundingClientRect().top
	return Array.from(bar.querySelectorAll('*')).some(element => {
		const box = element.getBoundingClientRect()
		return (
			box.height > 0 && box.top < top - POPUP_REACH && isShown(element, bar)
		)
	})
}

/** Focus that came from the keyboard; a mouse click focuses buttons too. */
const isKeyboardFocus = (target: EventTarget): boolean => {
	try {
		return target instanceof Element && target.matches(':focus-visible')
	} catch {
		return false
	}
}

const HotZone = styled.div`
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	height: ${HOT_ZONE_HEIGHT}px;
	z-index: 3;
`

const Bar = styled.div<{ revealed: boolean }>`
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 4;
	transform: translateY(
		${props => (props.revealed ? '0' : `calc(100% - ${HANDLE_HEIGHT}px)`)}
	);
	transition: transform 0.25s ease-out;

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`

const Handle = styled.div`
	position: absolute;
	left: 50%;
	transform: translateX(-50%);
	top: -${HANDLE_HEIGHT}px;
	width: 96px;
	height: ${HANDLE_HEIGHT}px;
	border-radius: ${HANDLE_HEIGHT}px ${HANDLE_HEIGHT}px 0 0;
	background-color: ${colors.lightPurple};
`

type Props = {
	children: React.ReactNode
}

/**
 * Overlays the room menu on the stage instead of reserving height for it, so
 * an in-person projector gives the whole room to the content.
 *
 * A wrapper rather than a change to `Menu`: the lobby, the group room, the
 * public chat and both online rooms rely on `Menu` being an in-flow flex
 * child, and only the in-person trainer shell wants it floating.
 *
 * Three things keep it from disappearing under the trainer's hand, live, in
 * front of a room:
 *
 * - **Hover at the edge.** A 16px strip along the bottom edge wakes it - the
 *   stage keeps that strip free (`AUTO_HIDE_MENU_RESERVED_HEIGHT`), so the
 *   zone never covers a stage control - and it stays awake while the pointer
 *   is anywhere in its subtree. Menu items open
 *   `PopupMenu`, `MenuItemBubble` and `ConfiguredSlideSwitcher` *above* the
 *   bar; those are DOM descendants, so hovering them counts as hovering the
 *   bar even though they are painted outside its box.
 * - **Pinned while something it opened is open.** Some controls open a
 *   panel that outlives the pointer - the slide switcher most of all. While
 *   one is on screen (`ownsOpenPopup`) the bar stays put, so it is not
 *   orphaned; once it closes, or a click lands outside, or Escape is pressed,
 *   the bar is free to hide. A control that starts something on the stage
 *   instead (Quiz, a vote) leaves nothing open, so the bar does not sit on
 *   top of the stage's own buttons afterwards.
 * - **Never fully gone.** A 4px handle stays on screen at rest. A trainer who
 *   cannot find the menu is stuck.
 *
 * Keyboard users keep it up while keyboard focus is inside it.
 */
export const AutoHideMenuBar: FC<Props> = ({ children }) => {
	const barRef = useRef<HTMLDivElement>(null)
	const closeTimer = useRef<ReturnType<typeof setTimeout>>()
	const [hovered, setHoveredState] = useState(false)
	/** The same, readable from the DOM observer (`checkPinnedAtRest`). */
	const hoveredRef = useRef(false)
	const setHovered = useCallback((value: boolean) => {
		hoveredRef.current = value
		setHoveredState(value)
	}, [])
	const [pinned, setPinned] = useState(false)
	const [keyboard, setKeyboard] = useState(false)
	const checkFrame = useRef<number>()
	/**
	 * Set by a click outside or Escape: the trainer sent the bar away while
	 * what it opened is still on screen - the slide switcher closes only
	 * through its own controls. Hiding changes the bar's class, which re-reads
	 * the DOM; without this that read would pin the bar straight back. Cleared
	 * once nothing is open, or by the next click on the bar.
	 */
	const dismissed = useRef(false)

	const cancelClose = useCallback(() => {
		if (closeTimer.current) {
			clearTimeout(closeTimer.current)
			closeTimer.current = undefined
		}
	}, [])

	/** Re-reads whether a popup is open, once the DOM has settled. */
	const checkPinned = useCallback(() => {
		if (checkFrame.current !== undefined) {
			return
		}
		checkFrame.current = requestAnimationFrame(() => {
			checkFrame.current = undefined
			const bar = barRef.current
			const open = !!bar && ownsOpenPopup(bar)
			if (!open) {
				dismissed.current = false
			}
			setPinned(open && !dismissed.current)
		})
	}, [])

	/**
	 * For the DOM's own changes. While the pointer is on the bar it is up
	 * anyway, so whether a popup pins it does not matter yet - and that is
	 * when the DOM churns most, every hover bubble fading in and out. The walk
	 * over the bar's layout waits for the pointer to leave (or a click)
	 * instead of running on each of those changes.
	 */
	const checkPinnedAtRest = useCallback(() => {
		if (!hoveredRef.current) {
			checkPinned()
		}
	}, [checkPinned])

	const onEnter = useCallback(() => {
		cancelClose()
		setHovered(true)
	}, [cancelClose, setHovered])

	// Popups opened by hover fade out with it; by the end of the delay only
	// the ones opened by a click are left to keep the bar up.
	//
	// A clicked menu button keeps focus, and hover popups also show on
	// `:focus-within` - so they stayed up, kept the bar pinned and covered the
	// slide for a trainer advancing with a keyboard or a clicker. A button
	// focused by the mouse lets go here; keyboard focus, and a text field the
	// trainer is typing in, are left alone.
	const onLeave = useCallback(() => {
		cancelClose()
		const focused = document.activeElement
		if (
			focused instanceof HTMLButtonElement &&
			barRef.current?.contains(focused) &&
			!isKeyboardFocus(focused)
		) {
			focused.blur()
		}
		closeTimer.current = setTimeout(() => {
			setHovered(false)
			checkPinned()
		}, CLOSE_DELAY_MS)
	}, [cancelClose, checkPinned, setHovered])

	useEffect(() => cancelClose, [cancelClose])

	// Popups open after a click, after a fetch, or on hover; and they close
	// the same ways. Watching the subtree catches all of them.
	useEffect(() => {
		const bar = barRef.current
		if (!bar) {
			return
		}
		const observer = new MutationObserver(checkPinnedAtRest)
		observer.observe(bar, {
			subtree: true,
			childList: true,
			attributes: true,
			attributeFilter: ['style', 'class', 'hidden'],
		})
		return () => {
			observer.disconnect()
			if (checkFrame.current !== undefined) {
				cancelAnimationFrame(checkFrame.current)
				checkFrame.current = undefined
			}
		}
	}, [checkPinnedAtRest])

	useEffect(() => {
		if (!pinned) {
			return
		}

		const unpin = () => {
			dismissed.current = true
			setPinned(false)
		}
		const unpinOnOutsideClick = (event: MouseEvent) => {
			const target = event.target
			if (target instanceof Node && !barRef.current?.contains(target)) {
				unpin()
			}
		}
		const unpinOnEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				unpin()
			}
		}

		document.addEventListener('mousedown', unpinOnOutsideClick)
		document.addEventListener('keydown', unpinOnEscape)
		return () => {
			document.removeEventListener('mousedown', unpinOnOutsideClick)
			document.removeEventListener('keydown', unpinOnEscape)
		}
	}, [pinned])

	return (
		<>
			{/*
			 * `onMouseLeave` as well as `onMouseEnter`: the zone is taller than the
			 * bar's resting sliver and
			 * the revealed bar is only as tall as the menu, so a pointer that
			 * enters the zone above the bar and leaves upwards never crosses
			 * `Bar` and never fires its `mouseleave`. Without this the menu stays
			 * up over the projector - the one thing this component exists to
			 * prevent.
			 */}
			<HotZone
				onMouseEnter={onEnter}
				onMouseLeave={onLeave}
				data-testid="in_person.menu.hot_zone"
			/>
			<Bar
				ref={barRef}
				revealed={hovered || pinned || keyboard}
				onMouseEnter={onEnter}
				onMouseLeave={onLeave}
				onClick={() => {
					dismissed.current = false
					checkPinned()
				}}
				// Popups fade in and out; their opacity settles without a DOM
				// mutation, so a check mid-fade is read again once it ends.
				onTransitionEnd={checkPinnedAtRest}
				onFocus={event => {
					if (isKeyboardFocus(event.target)) {
						setKeyboard(true)
					}
				}}
				onBlur={event => {
					const next = event.relatedTarget
					if (!(next instanceof Node && barRef.current?.contains(next))) {
						setKeyboard(false)
					}
				}}
				data-state={hovered || pinned || keyboard ? 'revealed' : 'resting'}
				data-testid="in_person.menu.bar"
			>
				<Handle />
				{children}
			</Bar>
		</>
	)
}
